import navFailSound from '../../assets/audio/Trap_00.wav';
import { random } from 'lodash';

import { TLocation } from '../locations/location.generators';
import { ImmerStateCreator } from './store.types';

export const HeroAttackType = {
   Slash: 'Slash',
   Strike: 'Strike',
   Cleave: 'Cleave',
   DrinkPotion: 'DrinkPotion',
   Guard: 'Guard',
   ShieldBash: 'ShieldBash',
} as const;
export type HeroAttackType = (typeof HeroAttackType)[keyof typeof HeroAttackType];

type TAttackTypeConfig = {
   minAttack: number;
   maxAttack: number;
   actionPointsUsed: number;
};

type THeroState = {
   state: {
      maxHealth: number;
      currentHealth: number;
      maxActionPoints: number;
      currentActionPoints: number;
      armor: number;
      isDead: boolean;
      attackTypes: {
         [key in HeroAttackType]: TAttackTypeConfig;
      };
      wayfindingSkill: boolean;
      combatSpeedSkill: number;

      potionMaxCapacity: number;
      potionAvailable: number;

      attackSkillsUnlocked: number[];
      defenceSkillsUnlocked: number[];
      fortitudeSkillsUnlocked: number[];
      combatSpeedSkillsUnlocked: number[];
      potionCapacitySkillsUnlocked: number[];

      currentGuardAmount: number;
   };
};

type THeroActions = {
   actions: {
      heroicActions: {
         drinkPotion(): void;
         guard(): void;

         sufferDamage(damage: number): void;
         resetActionPoints(): void;
         useActionPointsWhenAttacking(params: { attackType: HeroAttackType }): void;

         attemptToNavigateSafely(currentLocation: TLocation): void;
         recoverHealth(amount: number): void;
         pickUpPotion(): void;
      };
      heroUnlockActions: {
         buyNextAttackSkill(): void;
         buyNextDefenceSkill(): void;
         buyNextFortitudeSkill(): void;
         buyWayfindingSkill(): void;
         buyNextCombatSpeedSkill(): void;
         buyNextPotionCapacitySkill(): void;
         buyPotion(): void;
      };
   };
};

type THeroGetters = {
   getters: {
      getAttackTypeConfig(params: { attackType: HeroAttackType }): TAttackTypeConfig;
   };
};

type THeroSlice = THeroState & THeroActions & THeroGetters;
export type TNamedHeroSlice = { hero: THeroSlice };

export const getHeroInitialState = (): THeroState['state'] => {
   return {
      maxHealth: 20,
      currentHealth: 20,
      maxActionPoints: 5,
      currentActionPoints: 5,
      armor: 0,
      isDead: false,
      attackTypes: {
         [HeroAttackType.Slash]: {
            minAttack: 8,
            maxAttack: 12,
            actionPointsUsed: 1,
         },
         [HeroAttackType.Strike]: {
            minAttack: 20,
            maxAttack: 30,
            actionPointsUsed: 2,
         },
         [HeroAttackType.Cleave]: {
            minAttack: 32,
            maxAttack: 42,
            actionPointsUsed: 3,
         },
         [HeroAttackType.DrinkPotion]: {
            minAttack: 0,
            maxAttack: 0,
            actionPointsUsed: 1,
         },
         [HeroAttackType.Guard]: {
            minAttack: 0,
            maxAttack: 0,
            actionPointsUsed: 2,
         },
         [HeroAttackType.ShieldBash]: {
            minAttack: 3,
            maxAttack: 7,
            actionPointsUsed: 3,
         },
      },
      wayfindingSkill: false,
      combatSpeedSkill: 0,
      potionMaxCapacity: 1,
      potionAvailable: 1,

      attackSkillsUnlocked: [],
      defenceSkillsUnlocked: [],
      fortitudeSkillsUnlocked: [],
      combatSpeedSkillsUnlocked: [],
      potionCapacitySkillsUnlocked: [],

      currentGuardAmount: 0,
   }
};

export const createHeroSlice: ImmerStateCreator<TNamedHeroSlice, THeroSlice> = (set, get, store) => ({
   /***************************
    * State
    ***************************/
   state: getHeroInitialState(),

   /***************************
    * Actions
    ***************************/
   actions: {
      heroicActions: {
         drinkPotion: () => {
            return set((state) => {
               const currentPotions = state.hero.state.potionAvailable;
               state.hero.state.potionAvailable = currentPotions - 1;
               let newHealth = state.hero.state.currentHealth + 10;
               if (newHealth > state.hero.state.maxHealth) {
                  newHealth = state.hero.state.maxHealth;
               }
               state.hero.state.currentHealth = newHealth;
               const currentAP = state.hero.state.currentActionPoints;
               state.hero.state.currentActionPoints = currentAP - state.hero.state.attackTypes.DrinkPotion.actionPointsUsed;
            });
         },

         guard: () => {
            return set((state) => {
               const guardAmount = random(5, 10);
               state.hero.state.currentGuardAmount = guardAmount;

               const currentAP = state.hero.state.currentActionPoints;
               state.hero.state.currentActionPoints = currentAP - state.hero.state.attackTypes.Guard.actionPointsUsed;
            });
         },

         sufferDamage: (damage: number) => {
            return set((state) => {
               const remainingDamage = damage - (state.hero.state.currentGuardAmount + state.hero.state.armor);
               if (remainingDamage > 0) {
                  let newHealth = state.hero.state.currentHealth - remainingDamage;
                  if (newHealth <= 0) {
                     newHealth = 0;
                     state.hero.state.isDead = true;
                  }
                  state.hero.state.currentHealth = newHealth;
               }
               state.hero.state.currentGuardAmount = 0;
            });
         },

         resetActionPoints() {
            return set((state) => {
               state.hero.state.currentActionPoints = state.hero.state.maxActionPoints;
            });
         },

         useActionPointsWhenAttacking(params: { attackType: HeroAttackType }) {
            return set((state) => {
               const attackTypeConfig = get().hero.getters.getAttackTypeConfig({ attackType: params.attackType });
               let newActionPoints = state.hero.state.currentActionPoints - attackTypeConfig.actionPointsUsed;
               if (newActionPoints < 0) {
                  newActionPoints = 0;
               }
               state.hero.state.currentActionPoints = newActionPoints;
            });
         },

         attemptToNavigateSafely(currentLocation: TLocation) {
            const diceRoll = random(0, 6);

            if (diceRoll <= currentLocation.envRisk!) {
               if (get().hero.state.wayfindingSkill) {
                  const secondDiceRoll = random(0, 6);
                  if (secondDiceRoll <= currentLocation.envRisk!) {
                     new Audio(navFailSound).play();
                     get().hero.actions.heroicActions.sufferDamage(currentLocation.envDamage!);
                  }
               }
               new Audio(navFailSound).play();
               get().hero.actions.heroicActions.sufferDamage(currentLocation.envDamage!);
            }
         },
         recoverHealth(amount) {
            return set((state) => {
               let newHealth = state.hero.state.currentHealth + amount;
               if (newHealth > state.hero.state.maxHealth) {
                  newHealth = state.hero.state.maxHealth;
               }
               state.hero.state.currentHealth = newHealth;
            });
         },

         pickUpPotion() {
            return set((state) => {
               const canPickupPotion = state.hero.state.potionAvailable < state.hero.state.potionMaxCapacity;
               if (canPickupPotion) {
                  state.hero.state.potionAvailable = state.hero.state.potionAvailable + 1;
               }
            });
         },
      },

      heroUnlockActions: {
         buyNextAttackSkill() {
            const attackSkills = get().hero.state.attackSkillsUnlocked;
            const maxAttackSkill = 3;

            if (attackSkills.length < maxAttackSkill) {
               const nextAttackSkillNumber = attackSkills.length + 1;

               return set((state) => {
                  (state.hero as any)[`hasUnlockedAttackSkillLevel${nextAttackSkillNumber}`] = true;
                  state.hero.state.attackSkillsUnlocked = [...attackSkills, nextAttackSkillNumber];
               });
            }
         },

         buyNextDefenceSkill() {
            const defenceSkills = get().hero.state.defenceSkillsUnlocked;
            const maxDefenceSkill = 3;

            if (defenceSkills.length < maxDefenceSkill) {
               const nextDefenceSkillNumber = defenceSkills.length + 1;

               return set((state) => {
                  (state.hero as any)[`hasUnlockedDefenceSkillLevel${nextDefenceSkillNumber}`] = true;
                  state.hero.state.defenceSkillsUnlocked = [...defenceSkills, nextDefenceSkillNumber];

                  if (state.hero.state.defenceSkillsUnlocked.length === 1) {
                     state.hero.state.armor = 1;
                  }
                  if (state.hero.state.defenceSkillsUnlocked.length === 2) {
                     state.hero.state.armor = 2;
                  }
                  if (state.hero.state.defenceSkillsUnlocked.length === 3) {
                     state.hero.state.armor = 3;
                  }
               });
            }
         },

         buyNextFortitudeSkill() {
            const fortitudeSkills = get().hero.state.fortitudeSkillsUnlocked;
            const maxFortitudeSkill = 5;

            if (fortitudeSkills.length < maxFortitudeSkill) {
               const nextFortitudeSkillNumber = fortitudeSkills.length + 1;

               return set((state) => {
                  (state.hero as any)[`hasUnlockedFortitudeSkillLevel${nextFortitudeSkillNumber}`] = true;
                  state.hero.state.fortitudeSkillsUnlocked = [...fortitudeSkills, nextFortitudeSkillNumber];
                  state.hero.state.maxHealth = state.hero.state.maxHealth + 1;
                  state.hero.state.currentHealth = state.hero.state.maxHealth;
               });
            }
         },

         buyWayfindingSkill() {
            return set((state) => {
               state.hero.state.wayfindingSkill = true;
            });
         },

         buyNextCombatSpeedSkill() {
            const combatSpeedSkills = get().hero.state.combatSpeedSkillsUnlocked;
            const maxCombatSpeedSkill = 2;

            if (combatSpeedSkills.length < maxCombatSpeedSkill) {
               const nextCombatSpeedSkillNumber = combatSpeedSkills.length + 1;

               return set((state) => {
                  (state.hero as any)[`hasUnlockedCombatSpeedSkillLevel${nextCombatSpeedSkillNumber}`] = true;
                  state.hero.state.combatSpeedSkillsUnlocked = [...combatSpeedSkills, nextCombatSpeedSkillNumber];

                  if (state.hero.state.combatSpeedSkillsUnlocked.length === 1) {
                     state.hero.state.maxActionPoints = 6;
                     state.hero.state.currentActionPoints = 6;
                  }
                  if (state.hero.state.combatSpeedSkillsUnlocked.length === 2) {
                     state.hero.state.maxActionPoints = 7;
                     state.hero.state.currentActionPoints = 7;
                  }
               });
            }
         },

         buyNextPotionCapacitySkill() {
            const potionCapacity = get().hero.state.potionCapacitySkillsUnlocked;
            const maxPotionCapacity = 2;

            if (potionCapacity.length < maxPotionCapacity) {
               const nextPotionCapacitySkillNumber = potionCapacity.length + 1;

               set((state) => {
                  (state.hero as any)[`hasUnlockedPotionCapacitySkillLevel${nextPotionCapacitySkillNumber}`] = true;
                  state.hero.state.potionCapacitySkillsUnlocked = [...potionCapacity, nextPotionCapacitySkillNumber];
               });
            }

            if (get().hero.state.potionCapacitySkillsUnlocked.length === 1) {
               set((state) => {
                  state.hero.state.potionMaxCapacity = 2;
               });
            }
            if (get().hero.state.potionCapacitySkillsUnlocked.length === 2) {
               set((state) => {
                  state.hero.state.potionMaxCapacity = 3;
               });
            }
         },

         buyPotion() {
            return set((state) => {
               state.hero.state.potionAvailable = get().hero.state.potionAvailable + 1;
            });
         },
      },
   },

   /***************************
    * Getters
    ***************************/
   getters: {
      getAttackTypeConfig(params: { attackType: HeroAttackType }): TAttackTypeConfig {
         return get().hero.state.attackTypes[params.attackType];
      },
   },
});
